import { Button, Label, toast } from '@startupquickstart/frontend';
import { Upload, Video as VideoIcon, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useVideoUpload } from '@/hooks/useVideoUpload';

interface VideoUploadDropzoneProps {
  /** Current media id (the stored video), or undefined when none. */
  value?: string;
  onChange: (mediaId: string | undefined) => void;
  label?: string;
  required?: boolean;
  error?: string;
}

/** Build the playable URL for a stored media id. */
const getPreviewUrl = (id: string) => `/api/v1/media/${id}`;

/**
 * Drag-and-drop video upload that returns a media id. Uploads the file straight
 * to S3 via a presigned URL (see {@link useVideoUpload}) and previews it with an
 * HTML5 `<video>`.
 *
 * @param props - {@link VideoUploadDropzoneProps}.
 */
export function VideoUploadDropzone({
  value,
  onChange,
  label = 'Video',
  required = false,
  error,
}: VideoUploadDropzoneProps) {
  const { uploadVideo, isUploading, progress } = useVideoUpload();
  const [isDragging, setIsDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(
    value ? getPreviewUrl(value) : undefined,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPreviewUrl(value ? getPreviewUrl(value) : undefined);
  }, [value]);

  const handleFileSelect = useCallback(
    async (file: File) => {
      if (!file.type.startsWith('video/')) {
        toast.error('Please upload a video file');
        return;
      }
      try {
        const mediaId = await uploadVideo(file);
        onChange(mediaId);
        setPreviewUrl(getPreviewUrl(mediaId));
        toast.success('Video uploaded successfully');
      } catch (err) {
        toast.error((err as Error)?.message || 'Failed to upload video');
      }
    },
    [onChange, uploadVideo],
  );

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleRemove = () => {
    onChange(undefined);
    setPreviewUrl(undefined);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClick = () => fileInputRef.current?.click();

  return (
    <div className="grid gap-2">
      {label && (
        <Label>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        onChange={handleInputChange}
        className="hidden"
      />

      {previewUrl && !isUploading ? (
        <div className="relative group">
          <div className="relative overflow-hidden rounded-xl border-2 border-border bg-black flex items-center justify-center">
            <video src={previewUrl} controls className="max-h-96 w-auto rounded-xl" />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 pointer-events-none">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleClick}
                className="pointer-events-auto"
              >
                <Upload className="h-4 w-4 mr-2" />
                Replace
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={handleRemove}
                className="pointer-events-auto"
              >
                <X className="h-4 w-4 mr-2" />
                Remove
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div
          onClick={handleClick}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragging(false);
          }}
          onDrop={handleDrop}
          className={`relative cursor-pointer rounded-xl border-2 border-dashed transition-all h-48 flex flex-col items-center justify-center gap-3 p-6 ${
            isDragging ? 'border-primary bg-primary/5' : 'border-border bg-muted/50'
          } ${isUploading ? 'pointer-events-none' : 'hover:border-primary hover:bg-primary/5'}`}
        >
          {isUploading ? (
            <>
              <Upload className="h-12 w-12 text-muted-foreground animate-pulse" />
              <div className="w-full max-w-xs space-y-2">
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-sm text-muted-foreground text-center">
                  Uploading… {progress}%
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="rounded-full bg-primary/10 p-4">
                <VideoIcon className="h-8 w-8 text-primary" />
              </div>
              <div className="text-center space-y-1">
                <p className="text-sm font-medium">
                  <span className="text-primary">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-muted-foreground">MP4, MOV, or WebM</p>
              </div>
            </>
          )}
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
